import Decimal from 'decimal.js';
import isFinite from 'lodash/isFinite';
import toNumber from 'lodash/toNumber';

/**
 * These functions are meant for parsing/stringifying number strings from/for <input type="text"> tags everywhere.
 *
 * The main reason why these exist is to unify how we handle localisation based decimal separator (comma vs period).
 *
 * Thousands should not be grouped in inputs.
 */

export class NumberInputParseError extends Error {
  input: any;

  constructor(input: any) {
    super(`Could not parse input into number. Input was \`${input}\`.`);
    this.input = input;
  }
}

export function numberInputStringToNumber(input: string, throwOnEmptyString?: boolean): number {
  /**
   * NOTE: By default, empty string is parsed as `0` as that's what many JavaScript developers would assume.
   * If your empty string shouldn't be parsed to `0`, you can pass `true` for `throwOnEmptyString`.
   */

  if (throwOnEmptyString && input === '') {
    throw new NumberInputParseError(input);
  }

  const result = toNumber(input.replace(',', '.'));
  if (!isFinite(result)) {
    throw new NumberInputParseError(input);
  }
  return result;
}

export function numberToNumberInputString(num: number, options?: Intl.NumberFormatOptions): string {
  return num.toLocaleString(undefined, { useGrouping: false, ...options });
}

// Converts a decimal returned by the backend (eg. `"10.50"`) to an input string
export function decimalStringToNumberInputString(input: string, minimumFractionDigits: number): string {
  return parseFloat(input).toLocaleString(undefined, {
    useGrouping: false,
    minimumFractionDigits,
  });
}

// Converts a user inputted decimal (eg. `"10,50"`) to a decimal string that the backend can understand, eg. `"10.5"`.
// Throws if number cannot be parsed.
export function numberInputStringToDecimalString(input: string) {
  try {
    return new Decimal(input.replace(',', '.')).toString();
  } catch (_) {
    throw new Error(`Could not parse input into a decimal. Input was \`${input}\`.`);
  }
}

// Convert a MonetaryAmount object returned by the backend to an input string.
//
// Some examples:
// * EUR to decimal in English locale: `{ amount: "12.5", currency: "EUR" }` => `"12.50"`
// * EUR to decimal in Finnish locale: `{ amount: "12.5", currency: "EUR" }` => `"12,50"`
// * JPY to decimal (in any locale):   `{ amount: "1234", currency: "JPY" }` => `"1234"`
//
// Note that currently, the backend seems to return the correct amount of fraction digits for monetary amounts.
// This is weakly guaranteed, so best to ensure the correct amount of digits in the client.
export const monetaryAmountToNumberInputString = (value: { amount: string; currency: Currency }) => {
  return decimalStringToNumberInputString(value.amount, value.currency === 'JPY' ? 0 : 2);
};
